/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { pick } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import type {
  defaultAreaFilter,
  defaultDomainFilter,
  defaultPhenomenonFilter,
  TimeFilter,
  defaultAreaFilter as fallbackAreaFilter,
} from './filter-utils';
import {
  AllPhenomenaKeys,
  defaultEmptyFilter,
  defaultLevelFilter,
  defaultTimeFilter,
  Domain,
  domainLevels,
  domainPhenomena,
  getAreasFromWarnings,
  emptyFilterId,
  getFiltersFromWarnings,
  getSelectedOptions,
  getValue,
  initialFilters,
  isWarningWithinInterval,
  Level,
  updateState,
} from './filter-utils';
import type { Warning } from '../../store/warningsForm/types';
import { isExpireMessage } from '../../utils/warningUtils';

// To add incident back: see https://gitlab.com/opengeoweb/opengeoweb/-/merge_requests/5332
export interface PickedFilterState {
  source: Partial<typeof defaultEmptyFilter>;
  domain: Partial<typeof defaultDomainFilter>;
  phenomenon: Partial<typeof defaultPhenomenonFilter>;
  level: Partial<typeof defaultLevelFilter>;
  area: Partial<typeof defaultAreaFilter>;
  time: TimeFilter;
}
export interface FilterState extends PickedFilterState {
  source: typeof defaultEmptyFilter;
  domain: typeof defaultDomainFilter;
  phenomenon: typeof defaultPhenomenonFilter;
  level: typeof defaultLevelFilter;
  area: typeof fallbackAreaFilter;
  time: TimeFilter;
}

export type FilterKeyType = keyof FilterState;
export type OptionKey<T extends FilterKeyType> = keyof FilterState[T];

export type UpdateFilter = <T extends FilterKeyType>(
  filterKey: T | 'ALL',
  optionKey: OptionKey<T> | 'ALL',
  value: boolean,
  only?: boolean,
) => void;

export interface FilterHookState {
  allSelected: boolean;
  updateFilter: UpdateFilter;
  updateTimeFilter: (timeFilter: TimeFilter) => void;
  byFilterState: (warning: Warning) => boolean;
  filterState: PickedFilterState;
  timeFilter: TimeFilter;
}

const getLevelsForDomainFilter = (
  domainFilter: Partial<Record<Domain, boolean>>,
): Level[] => {
  return (Object.keys(domainFilter) as Domain[]).flatMap((domain) =>
    domainFilter[domain] ? domainLevels[domain] : [],
  );
};

export const getWarningDomain = (warning: Warning): Domain => {
  if (['SIG', 'AIR'].includes(warning.warningDetail.level)) {
    return Domain.aviation;
  }
  if (
    warning.type === 'maritimeWarningCoastal' ||
    warning.type === 'maritimeWarningNorthSea'
  ) {
    return Domain.maritime;
  }
  return Domain.public;
};

export const getPhenomenaForDomainFilter = (
  domainFilter: Record<Domain, boolean>,
): (keyof typeof AllPhenomenaKeys)[] => {
  return (Object.keys(domainFilter) as Domain[]).flatMap((domain) =>
    domainFilter[domain] ? domainPhenomena[domain] : [],
  ) as (keyof typeof AllPhenomenaKeys)[];
};

/** Get filter object
 * @param savedFilter The savedFilter with all options
 * @param visibleOptions The options that should be shown
 * @returns pickedFilterObject - The filter object with only the relevant options
 * @returns allSelected - A value for whether or not every visible option is selected
 */
const getFilterOptions = <T extends FilterKeyType>(
  savedFilter: FilterState[T],
  visibleOptions: OptionKey<T>[],
): [Partial<FilterState[T]>, boolean] => {
  const pickedFilterObject = pick(savedFilter, [
    emptyFilterId,
    ...visibleOptions,
  ]);

  const { allSelected } = getSelectedOptions(pickedFilterObject);
  return [pickedFilterObject, allSelected];
};

export const createWarningFilterPredicate = (
  warnings: Warning[],
  savedFilterState: FilterState,
): ((warning: Warning) => boolean) => {
  const savedDomainFilter = {
    ...initialFilters.domain,
    ...savedFilterState.domain,
  };
  const savedLevelFilter = {
    ...initialFilters.level,
    ...savedFilterState.level,
  };
  const savedPhenomenonFilter = {
    ...initialFilters.phenomenon,
    ...savedFilterState.phenomenon,
  };
  const savedSourceFilter = {
    ...initialFilters.source,
    ...savedFilterState.source,
  };
  const savedAreaFilter = {
    ...initialFilters.area,
    ...savedFilterState.area,
  };

  const relevantLevels = getLevelsForDomainFilter(savedDomainFilter);
  const [pickedLevelFilter] = getFilterOptions<'level'>(
    savedLevelFilter,
    relevantLevels,
  );

  const selectedDomainPhenomena = getPhenomenaForDomainFilter(
    savedDomainFilter as Record<Domain, boolean>,
  );
  const warningsPhenomena = getFiltersFromWarnings(warnings, 'phenomenon');
  const relevantPhenomena = selectedDomainPhenomena.filter((phenomenon) =>
    warningsPhenomena.includes(phenomenon),
  );
  const [pickedPhenomenaFilter] = getFilterOptions<'phenomenon'>(
    savedPhenomenonFilter,
    relevantPhenomena,
  );

  const relevantSources = getFiltersFromWarnings(
    warnings,
    'warningProposalSource',
  );
  const effectiveSourceFilter = {
    ...Object.fromEntries(relevantSources.map((key) => [key, true])),
    ...savedSourceFilter,
  };
  const relevantAreas = getAreasFromWarnings(
    warnings.filter((warning) => savedDomainFilter[getWarningDomain(warning)]),
  );
  const effectiveAreaFilter = {
    ...Object.fromEntries(relevantAreas.map((key) => [key, true])),
    ...savedAreaFilter,
  };

  return (warning: Warning): boolean => {
    if (!isWarningWithinInterval(warning, savedFilterState.time)) {
      return false;
    }
    const levelDomain = getWarningDomain(warning);

    if (!savedDomainFilter[levelDomain]) {
      return false;
    }
    const level = isExpireMessage(warning)
      ? Level.minor
      : getValue(Level, warning.warningDetail.level);
    if (!pickedLevelFilter[level]) {
      return false;
    }
    const phenomenon = getValue(
      AllPhenomenaKeys,
      warning.warningDetail.phenomenon,
    );
    if (
      !pickedPhenomenaFilter[phenomenon as keyof typeof pickedPhenomenaFilter]
    ) {
      return false;
    }
    const source =
      relevantSources.find(
        (candidateSource) =>
          candidateSource === warning.warningDetail.warningProposalSource,
      ) ?? emptyFilterId;
    if (!effectiveSourceFilter[source]) {
      return false;
    }
    const warningAreas =
      warning.warningDetail.areas
        ?.map((area) => area.objectName)
        .filter((areaName): areaName is string => Boolean(areaName)) ?? [];
    if (warningAreas.length === 0) {
      return Boolean(effectiveAreaFilter[emptyFilterId]);
    }
    if (!warningAreas.some((areaName) => effectiveAreaFilter[areaName])) {
      return false;
    }
    return true;
  };
};

export const useWarningFilters = (
  warnings: Warning[],
  onUpdateFilters: (
    filterState: Partial<FilterState>,
    systemUpdate?: boolean,
  ) => void,
  savedFilterState: FilterState,
  presetFilterState?: PickedFilterState,
): FilterHookState => {
  const {
    domain: savedDomainFilter = initialFilters.domain,
    phenomenon: savedPhenomenonFilter = initialFilters.phenomenon,
    level: persistedLevelFilter = initialFilters.level,
    area: savedAreaFilter = initialFilters.area,
    source: savedSourceFilter = initialFilters.source,
    time: savedTimeFilter = initialFilters.time,
    // incident: savedIncidentFilter,
  } = savedFilterState;
  const savedLevelFilter = {
    ...persistedLevelFilter,
    minor: persistedLevelFilter.minor ?? initialFilters.level.minor,
  };

  /** Levels for selected domain(s) */
  const relevantLevels = useMemo(
    () => getLevelsForDomainFilter(savedDomainFilter),
    [savedDomainFilter],
  );

  const [pickedLevelFilter, allLevelsSelected] = getFilterOptions<'level'>(
    savedLevelFilter,
    relevantLevels,
  );

  /** Phenomena for selected domain(s) */
  const domainPhenomena = useMemo(
    () =>
      getPhenomenaForDomainFilter(savedDomainFilter as Record<Domain, boolean>),
    [savedDomainFilter],
  );

  /** Phenomena in the warnings */
  const warningsPhenomena = useMemo(
    () => getFiltersFromWarnings(warnings, 'phenomenon'),
    [warnings],
  );

  const relevantPhenomena = useMemo(
    () =>
      domainPhenomena.filter((phenomenon) =>
        warningsPhenomena.includes(phenomenon),
      ),
    [warningsPhenomena, domainPhenomena],
  );

  const [pickedPhenomenaFilter, allPhenomenaSelected] =
    getFilterOptions<'phenomenon'>(savedPhenomenonFilter, relevantPhenomena);

  const relevantAreas = useMemo(
    () =>
      getAreasFromWarnings(
        warnings.filter(
          (warning) => savedDomainFilter[getWarningDomain(warning)],
        ),
      ),
    [savedDomainFilter, warnings],
  );

  const defaultAreaFilter = useMemo(
    () =>
      Object.fromEntries(
        relevantAreas.map((key) => {
          const savedValue = savedAreaFilter[key];
          const presetValue = presetFilterState?.area?.[key];
          return [key, savedValue ?? presetValue ?? true];
        }),
      ),
    [presetFilterState?.area, relevantAreas, savedAreaFilter],
  );

  const [pickedAreaFilter, allAreasSelected] = getFilterOptions<'area'>(
    defaultAreaFilter as FilterState['area'],
    relevantAreas,
  );

  // source
  const relevantSources = useMemo(
    () => getFiltersFromWarnings(warnings, 'warningProposalSource'),
    [warnings],
  );
  const defaultSourceFilter = useMemo(
    () =>
      Object.fromEntries(
        relevantSources.map((key) => {
          const presetValue = presetFilterState?.source?.[key];
          return [key, presetValue ?? true];
        }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relevantSources],
  );
  const { allSelected: allSourcesSelected } =
    getSelectedOptions(savedSourceFilter);

  const populatedIncidentAndSourceFilter: Partial<FilterState> = useMemo(
    () => ({
      // incident: defaultIncidentFilter,
      area: defaultAreaFilter,
      source: defaultSourceFilter,
    }),
    [defaultAreaFilter, defaultSourceFilter],
  );
  const initialFiltersWithPresets = useMemo(
    () =>
      ({
        source: defaultSourceFilter || defaultEmptyFilter,

        domain: {
          ...initialFilters.domain,
          ...presetFilterState?.domain,
        },

        phenomenon: {
          ...initialFilters.phenomenon,
          ...presetFilterState?.phenomenon,
        } as Record<string, boolean>,

        level: {
          ...initialFilters.level,
          ...presetFilterState?.level,
        },

        area: {
          ...initialFilters.area,
          ...presetFilterState?.area,
        },

        time: presetFilterState?.time ?? defaultTimeFilter,
      }) as FilterState,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presetFilterState],
  );
  useEffect(() => {
    onUpdateFilters(populatedIncidentAndSourceFilter, true);
  }, [populatedIncidentAndSourceFilter, onUpdateFilters]);

  useEffect(() => {
    // when the preset changes, update the filters
    onUpdateFilters(initialFiltersWithPresets, true);
  }, [initialFiltersWithPresets, onUpdateFilters]);

  const { allSelected: allDomainSelected } =
    getSelectedOptions(savedDomainFilter);

  const allSelected =
    allDomainSelected &&
    allLevelsSelected &&
    allAreasSelected &&
    allPhenomenaSelected &&
    allSourcesSelected;

  /**
   * @param filterKey - Name of the filter | "ALL" filters
   * @param optionKey - Name of the option | "ALL" options
   * @param value - The new value
   * @param only - Flag that decides if all other options should be disabled
   */
  const updateFilter: UpdateFilter = (filterKey, optionKey, value, only) => {
    const levelFilter = value ? defaultLevelFilter : defaultEmptyFilter;
    if (filterKey === 'ALL') {
      onUpdateFilters({
        domain: updateState(savedDomainFilter, 'ALL', value),
        level: updateState(levelFilter, 'ALL' as OptionKey<'level'>, value),
        phenomenon: updateState(
          savedPhenomenonFilter,
          'ALL' as OptionKey<'phenomenon'>,
          value,
        ),
        area: updateState(savedAreaFilter, 'ALL' as OptionKey<'area'>, value),
        // incident: updateState(savedIncidentFilter, 'ALL', value),
        source: updateState(savedSourceFilter, 'ALL', value),
      });
    } else if (filterKey === 'domain') {
      const newDomain = updateState(
        savedDomainFilter,
        optionKey as OptionKey<'domain'>,
        value,
        only,
      );
      onUpdateFilters({ domain: newDomain });
    } else if (filterKey === 'level') {
      onUpdateFilters({
        level: updateState(
          savedLevelFilter,
          optionKey as OptionKey<'level'>,
          value,
          only,
        ),
      });
    } else if (filterKey === 'phenomenon') {
      onUpdateFilters({
        phenomenon: updateState(
          savedPhenomenonFilter,
          optionKey as OptionKey<'phenomenon'>,
          value,
          only,
        ),
      });
    } else if (filterKey === 'area') {
      onUpdateFilters({
        area: updateState(
          savedAreaFilter,
          optionKey as OptionKey<'area'>,
          value,
          only,
        ),
      });
    } else if (filterKey === 'source') {
      onUpdateFilters({
        source: updateState(
          savedSourceFilter,
          optionKey as OptionKey<'source'>,
          value,
          only,
        ),
      });
    }
  };

  const updateTimeFilter = useCallback(
    (timeFilter: TimeFilter) => {
      onUpdateFilters({ time: timeFilter });
    },
    [onUpdateFilters],
  );

  const byFilterState = useMemo(
    () => createWarningFilterPredicate(warnings, savedFilterState),
    [warnings, savedFilterState],
  );

  return {
    allSelected,
    updateFilter,
    updateTimeFilter,
    byFilterState,
    filterState: {
      domain: savedDomainFilter,
      level: pickedLevelFilter,
      phenomenon: pickedPhenomenaFilter,
      area: pickedAreaFilter,
      source: savedSourceFilter,
      time: savedTimeFilter,
    },
    timeFilter: savedTimeFilter,
  };
};
